import { lazy, Suspense } from "react";

import { QueryClientProvider } from "@tanstack/react-query";
import { createBrowserHistory } from "history";
import CookieConsent from "react-cookie-consent";
import { Redirect, Route, Router, Switch } from "react-router-dom";

import { Alert, ToastContainer } from "@alpacahq/ui/old";

import { version } from "../package.json";
import AmplitudeProvider from "./AmplitudeProvider";
import { Intercom } from "./Intercom";
import { init as initSentry } from "./Sentry";
import { createAppQueryClient } from "./v2/api/queryClient";
import { ErrorBoundary } from "./v2/components/ErrorBoundary";
import { GuestOnly } from "./v2/components/GuestOnly";
import { Layout } from "./v2/components/Layout";
import { RequireAdvancedTradingDashboard } from "./v2/components/RequireAdvancedTradingDashboard";
import { RequireAuth } from "./v2/components/RequireAuth";
import { UnleashContextSync } from "./v2/helpers/featureFlags";
// Page imports
import AuthPage from "./v2/pages/auth";
import LogoutPage from "./v2/pages/auth/LogoutPage";
import ConnectAppDetails from "./v2/pages/connect/AppDetailsPage";
import ConnectAppPage from "./v2/pages/connect/AppPage";
import { ConnectSubmitApp } from "./v2/pages/connect/SubmitApp/SubmitApp.connector";
import DashboardPage from "./v2/pages/dashboard";
import ActivitiesPage from "./v2/pages/dashboard/account/activities";
import BalancesPage from "./v2/pages/dashboard/account/balances";
import ConfigurationsPage from "./v2/pages/dashboard/account/configurations/index";
import DocumentsPage from "./v2/pages/dashboard/account/documents";
import {
  CoinTransfersRoute,
  CoinTransfersWhitelistAddressRoute,
} from "./v2/pages/dashboard/coinTransfers/CoinTransfers.routes";
import {
  DigitalAssetsTransfersRoute,
  DigitalAssetsWhitelistAddressRoute,
} from "./v2/pages/dashboard/digitalAssetTransfers/DigitalAssetTransfers.routes";
import EntityAccountOpener from "./v2/pages/dashboard/entityOnboarding/pages/accountOpener";
import EntityAgreements from "./v2/pages/dashboard/entityOnboarding/pages/agreements";
import EntityAuthIndividualsUBOs from "./v2/pages/dashboard/entityOnboarding/pages/authIndividualsUBOs";
import EntityBusinessInfo from "./v2/pages/dashboard/entityOnboarding/pages/businessInfo";
import EntityCompleted from "./v2/pages/dashboard/entityOnboarding/pages/completed";
import EntityDocuments from "./v2/pages/dashboard/entityOnboarding/pages/documents";
import FpslOnboarding from "./v2/pages/dashboard/fpslOnboarding";
import AcatsAccountInfo from "./v2/pages/dashboard/funding/acats/accountInfo";
import AcatsAssetsHolding from "./v2/pages/dashboard/funding/acats/assetsHolding";
import AcatsOriginatingBroker from "./v2/pages/dashboard/funding/acats/originatingBroker";
import AcatsOverview from "./v2/pages/dashboard/funding/acats/overview";
import AcatsPartialTransfer from "./v2/pages/dashboard/funding/acats/partialTransfer";
import AcatsReview from "./v2/pages/dashboard/funding/acats/review";
import FundingDeposit from "./v2/pages/dashboard/funding/deposit";
import FundingDepositAch from "./v2/pages/dashboard/funding/deposit/ach";
import FundingDepositCurrencyCloud from "./v2/pages/dashboard/funding/deposit/currencyCloud";
import FundingDepositWire from "./v2/pages/dashboard/funding/deposit/wire/index";
import FundingHistory from "./v2/pages/dashboard/funding/history";
import FundingPage from "./v2/pages/dashboard/funding/index";
import FundingLinkAch from "./v2/pages/dashboard/funding/link/ach";
import FundingLinkAchOAuth from "./v2/pages/dashboard/funding/link/ach/oauth";
import FundingLinkCurrencyCloud from "./v2/pages/dashboard/funding/link/currencyCloud";
import FundingLinkWire from "./v2/pages/dashboard/funding/link/wire";
import FundingWithdraw from "./v2/pages/dashboard/funding/withdraw";
import FundingWithdrawAch from "./v2/pages/dashboard/funding/withdraw/ach";
import FundingWithdrawCurrencyCloud from "./v2/pages/dashboard/funding/withdraw/currencyCloud";
import FundingWithdrawWire from "./v2/pages/dashboard/funding/withdraw/wire";
import HighYieldCashEnrollAgreement from "./v2/pages/dashboard/highYieldCash/enroll/agreement";
import HighYieldCashEnrollCompleted from "./v2/pages/dashboard/highYieldCash/enroll/completed";
import HighYieldCashEnrollStart from "./v2/pages/dashboard/highYieldCash/enroll/start";
import IraOnboarding from "./v2/pages/dashboard/iraOnboarding";
import MarketDataPage from "./v2/pages/dashboard/marketData";
import MarketDataOverview from "./v2/pages/dashboard/marketData/overview";
import MarketDataSubscribe from "./v2/pages/dashboard/marketData/subscribe";
import OnboardingPage from "./v2/pages/dashboard/onboarding";
import OptionsAgreement from "./v2/pages/dashboard/optionsOptIn/pages/agreement";
import OptionsEntityAccount from "./v2/pages/dashboard/optionsOptIn/pages/entityAccount";
import OptionsFinancialProfile from "./v2/pages/dashboard/optionsOptIn/pages/financialProfile";
import OptionsInvestmentExperience from "./v2/pages/dashboard/optionsOptIn/pages/investmentExperience";
import OptionsSpreads from "./v2/pages/dashboard/optionsOptIn/pages/spreads";
import OptionsStatus from "./v2/pages/dashboard/optionsOptIn/pages/status";
import OrdersPage from "./v2/pages/dashboard/orders";
import ProgramInterestPage from "./v2/pages/dashboard/ProgramInterest";
import PlansPage from "./v2/pages/dashboard/plans";
import PositionsPage from "./v2/pages/dashboard/positions";
import ProfilePage from "./v2/pages/dashboard/profile";
import TradePage from "./v2/pages/dashboard/tradeV2";
import TradingPlanPage from "./v2/pages/dashboard/tradingPlan";
import OAuthAuthorizationPage from "./v2/pages/oauth/AuthorizationPage";
import AuthxAuthorizationPage from "./v2/pages/oauth/AuthxAuthorizationPage";
import OrderPage from "./v2/pages/order/index";
import RootRedirect from "./v2/pages/RootRedirect";
import W8BENPage from "./v2/pages/sign/W8-BEN";
import SignupPage from "./v2/pages/signup";
import { Path, Redirects } from "./v2/path";
import { AccountProviders } from "./v2/providers/AccountProviders";
import { AuthProvider } from "./v2/providers/AuthProvider";
import { AdvancedTradingDashboardRoot } from "./v3-poc/pages";

// Lazy load devtools - only loaded when accessed in dev/staging
const Devtools = lazy(() => import("./v2/pages/devtools"));

import "./shims";
import { ADVANCED_TRADING_DASHBOARD_PATHS } from "./v3-poc/pages/path";

// BASE_URL always carries a trailing slash; history expects a basename without
// one, and treats "" as no basename, which is what a root deployment needs.
const history = createBrowserHistory({
  basename: import.meta.env.BASE_URL.replace(/\/$/, ""),
});

initSentry();

// @ts-expect-error adds version() command to global scope
window.version = () => {
  return import.meta.env.VITE_APP_VERSION || version;
};

const Routes = () => {
  return (
    <>
      <Router history={history}>
        <AuthProvider>
          <AccountProviders>
            <UnleashContextSync />
            <ErrorBoundary
              errorComponent={({ errorMessage }) => (
                <div className="flex flex-col p-4">
                  <Alert
                    variant="danger"
                    title="Something went wrong."
                    message={`Please refresh the page and try again or contact support. (error: ${errorMessage})`}
                  />
                </div>
              )}
            >
              <CookieConsent
                location="bottom"
                buttonText="Accept"
                cookieName="gdpr_accepted"
                style={{ background: "#2B373B" }}
                buttonStyle={{
                  color: "#4e503b",
                  fontSize: "13px",
                  marginRight: "7em",
                }}
              >
                Alpaca and selected third parties use cookies on this website as
                specified in the{" "}
                <a
                  href="https://files.alpaca.markets/disclosures/CookiePolicy.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-yellow-400"
                >
                  cookie policy
                </a>
                . You accept the use of these cookies or other identifiers by
                pressing 'Accept', closing this notice, or logging into your
                account.
              </CookieConsent>
              <Switch>
                {/* Public routes */}
                <Route path={Path.ROUTE_LOGIN} component={AuthPage} />
                {Object.entries(Redirects).map(([from, to]) => (
                  <Route
                    key={from}
                    path={from}
                    component={() => <Redirect push to={to} />}
                  />
                ))}

                {/* Guest-only routes (redirect away if already logged in) */}
                <Route
                  path="/logout"
                  component={() => (
                    <GuestOnly>
                      <LogoutPage />
                    </GuestOnly>
                  )}
                />
                <Route path="/signup/success" render={() => <SignupPage />} />
                <Route
                  path={["/signup", "/signup/pending", "/verify"]}
                  render={() => (
                    <GuestOnly>
                      <SignupPage />
                    </GuestOnly>
                  )}
                />

                {/* Authenticated routes */}
                <Route
                  path="/"
                  render={() => (
                    <RequireAuth>
                      <Switch>
                        <Route
                          path={ADVANCED_TRADING_DASHBOARD_PATHS.ROOT}
                          render={() => (
                            <RequireAdvancedTradingDashboard>
                              <AdvancedTradingDashboardRoot />
                            </RequireAdvancedTradingDashboard>
                          )}
                        />
                        <Route
                          path={Path.ROUTE_SIGN_W8BEN}
                          component={() => <Layout component={W8BENPage} />}
                        />
                        <Route
                          path={Path.ROUTE_AUTHX_AUTHORIZE}
                          component={() => (
                            <Layout
                              noHeader={true}
                              disableAppMenu={true}
                              showBrandInHeader={true}
                              showSearchInHeader={false}
                              component={AuthxAuthorizationPage}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_OAUTH_AUTHORIZE}
                          component={() => (
                            <Layout
                              noHeader={true}
                              disableAppMenu={true}
                              showBrandInHeader={true}
                              showSearchInHeader={false}
                              component={OAuthAuthorizationPage}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_PROFILE}
                          component={() => (
                            <Layout
                              showSearchInHeader={true}
                              component={ProfilePage}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_ALPACA_TRADING_PLAN}
                          component={() => (
                            <Layout
                              showSearchInHeader={true}
                              component={TradingPlanPage}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_HIGH_YIELD_ENROLL_COMPLETED}
                          component={() => (
                            <Layout
                              showSearchInHeader={false}
                              disableAppMenu
                              noHeader
                              component={HighYieldCashEnrollCompleted}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_HIGH_YIELD_ENROLL_AGREEMENT}
                          component={() => (
                            <Layout
                              showSearchInHeader={false}
                              disableAppMenu
                              noHeader
                              component={HighYieldCashEnrollAgreement}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_HIGH_YIELD_ENROLL}
                          component={() => (
                            <Layout
                              showSearchInHeader={false}
                              disableAppMenu
                              noHeader
                              component={HighYieldCashEnrollStart}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_FPSL_ONBOARDING_DYNAMIC_PAGE}
                          component={() => (
                            <Layout
                              showSearchInHeader={false}
                              noHeader={true}
                              disableAppMenu={true}
                              component={FpslOnboarding}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_FPSL_ONBOARDING}
                          component={() => (
                            <Layout
                              showSearchInHeader={false}
                              noHeader={true}
                              disableAppMenu={true}
                              component={FpslOnboarding}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_BALANCES}
                          component={() => <Layout component={BalancesPage} />}
                        />
                        <Route
                          path={Path.ROUTE_PROGRAM_INTEREST}
                          component={() => (
                            <Layout
                              noHeader={true}
                              component={ProgramInterestPage}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_DASHBOARD}
                          component={() => <Layout component={DashboardPage} />}
                        />
                        <Route
                          path={Path.ROUTE_NEW_ACCOUNT_DYNAMIC_PAGE}
                          component={() => (
                            <Layout
                              showSearchInHeader={true}
                              noHeader={false}
                              component={OnboardingPage}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_NEW_ACCOUNT}
                          component={() => (
                            <Layout
                              showSearchInHeader={true}
                              noHeader={false}
                              component={OnboardingPage}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_ENTITY_ONBOARDING_BUSINESS_INFO}
                          component={() => (
                            <Layout
                              showSearchInHeader={true}
                              noHeader={false}
                              component={EntityBusinessInfo}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_ENTITY_ONBOARDING_ACCOUNT_OPENER}
                          component={() => (
                            <Layout
                              showSearchInHeader={true}
                              noHeader={false}
                              component={EntityAccountOpener}
                            />
                          )}
                        />
                        <Route
                          path={
                            Path.ROUTE_ENTITY_ONBOARDING_AUTH_INDIVIDUALS_UBOS
                          }
                          component={() => (
                            <Layout
                              showSearchInHeader={true}
                              noHeader={false}
                              component={EntityAuthIndividualsUBOs}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_ENTITY_ONBOARDING_DOCUMENTS}
                          component={() => (
                            <Layout
                              showSearchInHeader={true}
                              noHeader={false}
                              component={EntityDocuments}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_ENTITY_ONBOARDING_AGREEMENTS}
                          component={() => (
                            <Layout
                              showSearchInHeader={true}
                              noHeader={false}
                              component={EntityAgreements}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_ENTITY_ONBOARDING_COMPLETED}
                          component={() => (
                            <Layout
                              showSearchInHeader={true}
                              noHeader={false}
                              component={EntityCompleted}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_IRA_ONBOARDING_DYNAMIC_PAGE}
                          component={() => (
                            <Layout
                              showSearchInHeader={false}
                              noHeader={true}
                              disableAppMenu={true}
                              component={IraOnboarding}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_IRA_ONBOARDING}
                          component={() => (
                            <Layout
                              showSearchInHeader={false}
                              noHeader={true}
                              disableAppMenu={true}
                              component={IraOnboarding}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_MARKET_DATA_SUBSCRIBE}
                          component={() => (
                            <Layout
                              showSearchInHeader={false}
                              noHeader={true}
                              disableAppMenu={true}
                              component={MarketDataSubscribe}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_MARKET_DATA_OVERVIEW}
                          component={() => (
                            <Layout component={MarketDataOverview} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_MARKET_DATA}
                          component={() => (
                            <Layout component={MarketDataPage} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_ORDER_DETAIL}
                          component={() => <Layout component={OrderPage} />}
                        />
                        <Route
                          path={Path.ROUTE_POSITIONS}
                          component={() => <Layout component={PositionsPage} />}
                        />
                        <Route
                          path={Path.ROUTE_ACCOUNT_ACTIVITIES}
                          component={() => (
                            <Layout component={ActivitiesPage} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_ORDERS}
                          component={() => <Layout component={OrdersPage} />}
                        />
                        <Route
                          path={Path.ROUTE_CONFIGURATION}
                          component={() => (
                            <Layout component={ConfigurationsPage} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_DOCUMENTS}
                          component={() => <Layout component={DocumentsPage} />}
                        />
                        <Route
                          path={Path.ROUTE_PLANS_AND_FEATURES}
                          component={() => <Layout component={PlansPage} />}
                        />
                        <Route
                          path={Path.ROUTE_FUNDING_DEPOSIT_WIRE}
                          component={() => (
                            <Layout component={FundingDepositWire} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_FUNDING_DEPOSIT_ACH}
                          component={() => (
                            <Layout component={FundingDepositAch} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_FUNDING_DEPOSIT_CURRENCYCLOUD}
                          component={() => (
                            <Layout component={FundingDepositCurrencyCloud} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_FUNDING_DEPOSIT}
                          component={() => (
                            <Layout component={FundingDeposit} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_FUNDING_WITHDRAW_ACH}
                          component={() => (
                            <Layout component={FundingWithdrawAch} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_FUNDING_WITHDRAW_WIRE}
                          component={() => (
                            <Layout component={FundingWithdrawWire} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_FUNDING_WITHDRAW_CURRENCYCLOUD}
                          component={() => (
                            <Layout component={FundingWithdrawCurrencyCloud} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_FUNDING_WITHDRAW}
                          component={() => (
                            <Layout component={FundingWithdraw} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_FUNDING_HISTORY}
                          component={() => (
                            <Layout component={FundingHistory} />
                          )}
                        />
                        {/* Plaid OAuth full-page-redirect return page. Its path
                            (ROUTE_FUNDING_LINK_ACH_OAUTH) is the legacy
                            /mobile/plaid/oauth-page.html so it matches the
                            Plaid-registered redirect_uri; just needs to resolve
                            before the catch-all `/` route below. */}
                        <Route
                          path={Path.ROUTE_FUNDING_LINK_ACH_OAUTH}
                          component={() => (
                            <Layout component={FundingLinkAchOAuth} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_FUNDING_LINK_ACH}
                          component={() => (
                            <Layout component={FundingLinkAch} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_FUNDING_LINK_WIRE}
                          component={() => (
                            <Layout component={FundingLinkWire} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_FUNDING_LINK_CURRENCYCLOUD}
                          component={() => (
                            <Layout component={FundingLinkCurrencyCloud} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_FUNDING}
                          component={() => <Layout component={FundingPage} />}
                        />
                        <Route
                          path={Path.ROUTE_PORTFOLIO_TRANSFER_REVIEW}
                          component={() => <Layout component={AcatsReview} />}
                        />
                        <Route
                          path={Path.ROUTE_PORTFOLIO_TRANSFER_PARTIAL_TRANSFER}
                          component={() => (
                            <Layout component={AcatsPartialTransfer} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_PORTFOLIO_TRANSFER_ASSETS_HOLDING}
                          component={() => (
                            <Layout component={AcatsAssetsHolding} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_PORTFOLIO_TRANSFER_ACCOUNT_INFO}
                          component={() => (
                            <Layout component={AcatsAccountInfo} />
                          )}
                        />
                        <Route
                          path={
                            Path.ROUTE_PORTFOLIO_TRANSFER_ORIGINATING_BROKER
                          }
                          component={() => (
                            <Layout component={AcatsOriginatingBroker} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_PORTFOLIO_TRANSFER}
                          component={() => <Layout component={AcatsOverview} />}
                        />
                        <Route
                          path={Path.ROUTE_TRADE_CRYPTO}
                          component={() => <Layout component={TradePage} />}
                        />
                        <Route
                          path={Path.ROUTE_TRADE}
                          component={() => <Layout component={TradePage} />}
                        />
                        <Route
                          path={Path.ROUTE_ALPACA_CONNECT_SUBMIT}
                          component={() => (
                            <Layout component={ConnectSubmitApp} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_ALPACA_CONNECT_EDIT}
                          component={() => (
                            <Layout component={ConnectSubmitApp} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_ALPACA_CONNECT_APP}
                          component={() => (
                            <Layout component={ConnectAppDetails} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_ALPACA_CONNECT}
                          component={() => (
                            <Layout component={ConnectAppPage} />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_CRYPTO_WHITELIST_ADDRESS}
                          component={CoinTransfersWhitelistAddressRoute}
                        />
                        <Route
                          path={Path.ROUTE_DIGITAL_ASSETS_WHITELIST_ADDRESS}
                          component={DigitalAssetsWhitelistAddressRoute}
                        />
                        <Route
                          path={Path.ROUTE_CRYPTO_TRANSFERS}
                          component={CoinTransfersRoute}
                        />
                        <Route
                          path={Path.ROUTE_DIGITAL_ASSETS_TRANSFERS}
                          component={DigitalAssetsTransfersRoute}
                        />
                        <Route
                          path={Path.ROUTE_OPTIONS_OPT_IN_FINANCIAL_PROFILE}
                          component={() => (
                            <Layout
                              showSearchInHeader={true}
                              noHeader={false}
                              component={OptionsFinancialProfile}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_OPTIONS_OPT_IN_INVESTMENT_EXPERIENCE}
                          component={() => (
                            <Layout
                              showSearchInHeader={true}
                              noHeader={false}
                              component={OptionsInvestmentExperience}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_OPTIONS_OPT_IN_SPREADS}
                          component={() => (
                            <Layout
                              showSearchInHeader={true}
                              noHeader={false}
                              component={OptionsSpreads}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_OPTIONS_OPT_IN_AGREEMENT}
                          component={() => (
                            <Layout
                              showSearchInHeader={true}
                              noHeader={false}
                              component={OptionsAgreement}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_OPTIONS_OPT_IN_STATUS}
                          component={() => (
                            <Layout
                              showSearchInHeader={true}
                              noHeader={false}
                              component={OptionsStatus}
                            />
                          )}
                        />
                        <Route
                          path={Path.ROUTE_OPTIONS_OPT_IN_ENTITY_ACCOUNT}
                          component={() => (
                            <Layout
                              showSearchInHeader={true}
                              noHeader={false}
                              component={OptionsEntityAccount}
                            />
                          )}
                        />
                        {["DEV", "STG"].includes(window.env.ENV) && (
                          <Route
                            path="/devtools"
                            component={() => (
                              <Suspense fallback={<div>Loading...</div>}>
                                <Layout component={Devtools} />
                              </Suspense>
                            )}
                          />
                        )}
                        <Route path="/" component={() => <RootRedirect />} />
                      </Switch>
                    </RequireAuth>
                  )}
                />
              </Switch>
            </ErrorBoundary>
            <Intercom />
          </AccountProviders>
        </AuthProvider>
      </Router>
      <ToastContainer position="top-right" className="text-sm" />
    </>
  );
};

const queryClient = createAppQueryClient();

const App = () => (
  <AmplitudeProvider>
    <QueryClientProvider client={queryClient}>
      <Routes />
    </QueryClientProvider>
  </AmplitudeProvider>
);

export default App;
